import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Copy, Image as ImageIcon, MessageCircle, MoreHorizontal, Pencil, Send, SmilePlus, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import liveChatApi, { liveChatWsBaseUrl } from '../../../services/liveChatApi';
import { useTenantTheme } from '../../../contexts/TenantThemeContext';
import { NEON } from './playerHelpers';
import SafeImage from '../../../components/SafeImage';

const IPHONE_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😍', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️',
  '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰',
  '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱',
  '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👻', '💀',
  '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️',
  '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💪', '🫶', '❤️', '🧡', '💛', '💚',
  '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🔥', '💯', '✨',
  '⭐', '🌟', '💫', '🎉', '🎊', '🎁', '🏆', '🥇', '⚡', '💥', '💦', '💨', '👀', '🧠', '💎', '🍀', '🌹', '🌈'
];
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const parseChatDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value);
  const normalized = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatChatTime = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(date);
};

const localDateKey = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return [date.getFullYear(), date.getMonth(), date.getDate()].join('-');
};

const dateSeparatorLabel = (value, previousValue) => {
  const currentKey = localDateKey(value);
  if (!currentKey || currentKey === localDateKey(previousValue)) return '';
  const date = parseChatDate(value);
  const now = new Date();
  const todayKey = localDateKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (currentKey === todayKey) return 'Today';
  if (currentKey === localDateKey(yesterday)) return 'Yesterday';
  return new Intl.DateTimeFormat([], {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
};

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) {
    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${window.location.origin}${parsed.pathname}${parsed.search || ''}`;
      }
      if (parsed.pathname.startsWith('/api/chat/media/')) {
        return `${window.location.origin}${parsed.pathname}${parsed.search || ''}`;
      }
    } catch {
      return url;
    }
    return url;
  }
  if (url.startsWith('/api/chat/media/')) return url;
  if (url.startsWith('/api/uploads/')) return url;
  if (url.startsWith('/uploads/')) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const liveChatSocketUrl = (path) => {
  if (liveChatWsBaseUrl) return `${liveChatWsBaseUrl}${path}`;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
};

const messageListSignature = (items = []) => items.map(item => [
  item.id,
  item.body,
  item.deleted ? 'deleted' : '',
  item.edited_at || '',
  item.read_by_staff ? 'staff-read' : '',
  item.read_by_player ? 'player-read' : '',
  item.attachments?.length || 0,
].join(':')).join('|');

const AGENT_SUBJECT_PREFIX = 'Texted to agent ';
const agentNameFromSubject = (subject = '') => {
  if (typeof subject !== 'string' || !subject.startsWith(AGENT_SUBJECT_PREFIX)) return '';
  return subject.slice(AGENT_SUBJECT_PREFIX.length).trim();
};

const parseAgentTagFromBody = (body = '') => {
  if (typeof body !== 'string' || !body.startsWith(AGENT_SUBJECT_PREFIX)) {
    return { agentName: '', body: body || '' };
  }
  const [firstLine, ...rest] = body.split('\n');
  const agentName = firstLine.slice(AGENT_SUBJECT_PREFIX.length).trim();
  const cleanBody = rest.join('\n').replace(/^\n/, '');
  return { agentName, body: cleanBody };
};

const withAgentTag = (body, agentName) => {
  if (!agentName) return body;
  return `${AGENT_SUBJECT_PREFIX}${agentName}${body ? `\n\n${body}` : ''}`;
};

const normalizeAgentName = (value = '') => String(value || '').trim().toLowerCase();

const messageBelongsToAgent = (msg, agent, fallbackConversation) => {
  if (!agent?.id && !agent?.name) return true;
  const targetId = String(msg?.target_agent_id || '').trim();
  const selectedId = String(agent?.id || '').trim();
  if (selectedId && targetId && selectedId === targetId) return true;

  const parsed = parseAgentTagFromBody(msg?.body || '');
  const targetName = normalizeAgentName(
    msg?.target_agent_name
    || parsed.agentName
    || fallbackConversation?.preferred_agent_name
    || agentNameFromSubject(fallbackConversation?.subject)
  );
  const selectedName = normalizeAgentName(agent?.name);
  return Boolean(selectedName && targetName && selectedName === targetName);
};

export default function ChatPanel({ user, selectedAgent, onRead }) {
  const { theme } = useTenantTheme();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [activeReactionId, setActiveReactionId] = useState(null);
  const [expandedReactionId, setExpandedReactionId] = useState(null);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [messageMenuMode, setMessageMenuMode] = useState('controls');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [visibleTimeId, setVisibleTimeId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [agentTyping, setAgentTyping] = useState('');
  const inputRef = useRef(null);
  const photoRef = useRef(null);
  const wsRef = useRef(null);
  const onReadRef = useRef(onRead);
  const reconnectAllowedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reactionTimerRef = useRef(null);
  const previewHistoryRef = useRef(false);
  const typingTimerRef = useRef(null);
  const typingPulseTimerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const keyboardFocusTimerRef = useRef(null);

  useEffect(() => {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    const html = document.documentElement;
    const previousBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousHtml = {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';

    return () => {
      body.style.position = previousBody.position;
      body.style.top = previousBody.top;
      body.style.left = previousBody.left;
      body.style.right = previousBody.right;
      body.style.width = previousBody.width;
      body.style.overflow = previousBody.overflow;
      body.style.overscrollBehavior = previousBody.overscrollBehavior;
      html.style.overflow = previousHtml.overflow;
      html.style.overscrollBehavior = previousHtml.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      const viewport = window.visualViewport;
      const height = Math.max(320, Math.round(viewport?.height || window.innerHeight));
      const top = Math.max(0, Math.round(viewport?.offsetTop || 0));
      document.documentElement.style.setProperty('--player-chat-viewport-height', `${height}px`);
      document.documentElement.style.setProperty('--player-chat-viewport-top', `${top}px`);
    };
    syncViewport();
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);
    window.addEventListener('resize', syncViewport);
    window.addEventListener('orientationchange', syncViewport);
    return () => {
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('scroll', syncViewport);
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener('orientationchange', syncViewport);
      document.documentElement.style.removeProperty('--player-chat-viewport-height');
      document.documentElement.style.removeProperty('--player-chat-viewport-top');
    };
  }, []);

  useEffect(() => {
    onReadRef.current = onRead;
  }, [onRead]);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const res = await liveChatApi.post('/api/chat/conversations', {
          subject: selectedAgent?.name ? `Texted to agent ${selectedAgent.name}` : 'Website live support',
          agent_id: selectedAgent?.id || null,
          agent_name: selectedAgent?.name || null,
        });
        if (cancelled) return;
        const room = res.data.conversation;
        setConversation(room);
        await refreshMessages(room.id, { cancelled: () => cancelled });
        onReadRef.current?.();
      } catch {
        toast.error('Live chat is not available right now');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [selectedAgent?.id, selectedAgent?.name]);

  const refreshMessages = async (roomId, options = {}) => {
    try {
      const params = new URLSearchParams({ limit: '60' });
      if (selectedAgent?.id) params.set('agent_id', selectedAgent.id);
      if (selectedAgent?.name) params.set('agent_name', selectedAgent.name);
      const history = await liveChatApi.get(`/api/chat/conversations/${roomId}/messages?${params.toString()}`);
      if (options.cancelled?.()) return;
      const serverMessages = (history.data.messages || []).filter(msg => messageBelongsToAgent(msg, selectedAgent, history.data.conversation || conversation));
      setMessages(prev => {
        const pending = prev.filter(item => String(item.id || '').startsWith('temp_'));
        const next = [...serverMessages, ...pending].slice(-60);
        return messageListSignature(prev) === messageListSignature(next) ? prev : next;
      });
      onReadRef.current?.();
    } catch {
      if (!options.quiet) toast.error('Live chat is not available right now');
    }
  };

  useEffect(() => {
    if (!conversation?.id) return undefined;
    reconnectAllowedRef.current = true;
    const connect = () => {
      const token = localStorage.getItem('session_token');
      if (!reconnectAllowedRef.current || !token) return;
      const ws = new WebSocket(liveChatSocketUrl(`/ws/chat?token=${encodeURIComponent(token)}&conversation_id=${encodeURIComponent(conversation.id)}`));
      ws.onopen = () => {
        setWsConnected(true);
        ws._ping = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25000);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const msg = data.message;
          if ((data.type === 'message.created' || data.type === 'message.ack') && msg) {
            if (!messageBelongsToAgent(msg, selectedAgent, conversation)) return;
            setMessages(prev => {
              if (prev.some(item => item.id === msg.id)) return prev;
              return [...prev, msg].slice(-60);
            });
            onReadRef.current?.();
          }
          if (data.type === 'message.reaction' && msg) {
            setMessages(prev => prev.map(item => item.id === msg.id ? msg : item));
          }
          if ((data.type === 'message.updated' || data.type === 'message.deleted') && msg) {
            setMessages(prev => prev.map(item => item.id === msg.id ? msg : item));
          }
          if (data.type === 'typing' && data.sender_role === 'staff') {
            const name = data.agent_name || data.sender_name || selectedAgent?.name || conversation?.preferred_agent_name || 'Agent';
            setAgentTyping(data.is_typing ? name : '');
            if (data.is_typing) {
              clearTimeout(typingTimerRef.current);
              typingTimerRef.current = setTimeout(() => setAgentTyping(''), 3500);
            }
          }
          if (data.type === 'conversation.closed') {
            toast('Support closed this conversation');
          }
        } catch {}
      };
      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(ws._ping);
        if (reconnectAllowedRef.current) {
          reconnectTimer.current = setTimeout(connect, 2500);
        }
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };
    connect();
    return () => {
      reconnectAllowedRef.current = false;
      clearTimeout(reconnectTimer.current);
      clearTimeout(typingTimerRef.current);
      clearInterval(typingPulseTimerRef.current);
      clearTimeout(typingStopTimerRef.current);
      clearTimeout(keyboardFocusTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [conversation?.id, selectedAgent?.id, selectedAgent?.name]);

  useEffect(() => {
    if (!conversation?.id) return undefined;
    const interval = setInterval(() => {
      refreshMessages(conversation.id, { quiet: true });
    }, wsConnected ? 6000 : 2500);
    return () => clearInterval(interval);
  }, [conversation?.id, wsConnected, selectedAgent?.id, selectedAgent?.name]);

  const latestMessageKey = messages.length
    ? `${messages[messages.length - 1]?.id || ''}:${messages[messages.length - 1]?.created_at || ''}:${messages[messages.length - 1]?.edited_at || ''}:${messages[messages.length - 1]?.deleted_at || ''}`
    : '';

  useEffect(() => {
    if (latestMessageKey) {
      messagesEndRef.current?.scrollIntoView({ behavior: messages.length > 8 ? 'auto' : 'smooth' });
    }
  }, [latestMessageKey, messages.length]);

  useEffect(() => {
    const closeOnBack = () => {
      if (previewHistoryRef.current) {
        previewHistoryRef.current = false;
        setPhotoPreview(null);
      }
    };
    window.addEventListener('popstate', closeOnBack);
    return () => window.removeEventListener('popstate', closeOnBack);
  }, []);

  const uploadPhoto = async (selectedPhoto = photo) => {
    if (!selectedPhoto) return [];
    const form = new FormData();
    form.append('file', selectedPhoto);
    const res = await liveChatApi.post('/api/chat/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return [res.data.attachment.id];
  };

  const sendTypingState = (isTyping) => {
    if (wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
      target_agent_name: selectedAgent?.name || conversation?.preferred_agent_name || null,
      is_typing: Boolean(isTyping),
    }));
  };

  const stopTypingPulse = () => {
    clearInterval(typingPulseTimerRef.current);
    typingPulseTimerRef.current = null;
    sendTypingState(false);
  };

  const startTypingPulse = (value) => {
    if (!String(value || '').trim()) {
      stopTypingPulse();
      return;
    }
    sendTypingState(true);
    if (!typingPulseTimerRef.current) {
      typingPulseTimerRef.current = setInterval(() => sendTypingState(true), 850);
    }
    clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(stopTypingPulse, 1700);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInput(value);
    startTypingPulse(value);
  };

  const handleInputFocus = () => {
    clearTimeout(keyboardFocusTimerRef.current);
    keyboardFocusTimerRef.current = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, 320);
  };

  const sendMessage = async () => {
    if (!conversation?.id) return;
    const body = input.trim();
    const selectedPhoto = photo;
    if (!body && !selectedPhoto) return;
    const photoPreviewUrl = selectedPhoto ? URL.createObjectURL(selectedPhoto) : '';
    const activeAgentForMessage = selectedAgent?.name || conversation?.preferred_agent_name || agentNameFromSubject(conversation?.subject) || '';
    const serverBody = withAgentTag(body, activeAgentForMessage);
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const tempMessage = {
      id: tempId,
      conversation_id: conversation.id,
      tenant_id: conversation.tenant_id,
      sender_id: user?.id,
      sender_name: user?.username || user?.email || 'You',
      sender_avatar_url: user?.avatar_url || null,
      sender_role: 'player',
      body,
      attachments: selectedPhoto ? [{ id: 'pending', filename: selectedPhoto.name, url: photoPreviewUrl, preview_url: photoPreviewUrl }] : [],
      reactions: {},
      created_at: new Date().toISOString(),
      read_by_player: true,
      read_by_staff: false,
      target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
      target_agent_name: activeAgentForMessage || null,
      target_agent_title: selectedAgent?.title || conversation?.preferred_agent_title || '',
      target_agent_avatar_url: selectedAgent?.avatar_url || conversation?.preferred_agent_avatar_url || '',
    };
    setMessages(prev => [...prev, tempMessage].slice(-60));
    setInput('');
    stopTypingPulse();
    setPhoto(null);
    setEmojiOpen(false);
    if (photoRef.current) photoRef.current.value = '';
    inputRef.current?.focus();
    setSending(true);
    try {
      const attachmentIds = await uploadPhoto(selectedPhoto);
      const res = await liveChatApi.post(`/api/chat/conversations/${conversation.id}/messages`, {
        body: serverBody,
        attachment_ids: attachmentIds,
        target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
        target_agent_name: activeAgentForMessage || null,
      });
      setMessages(prev => prev.map(item => item.id === tempId ? res.data.message : item).slice(-60));
    } catch {
      setMessages(prev => prev.map(item => item.id === tempId ? { ...item, send_failed: true } : item));
      toast.error('Failed to send live chat message');
    } finally {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      setSending(false);
    }
  };

  const addEmoji = (emoji) => {
    setInput(value => `${value}${emoji}`);
    inputRef.current?.focus();
  };

  const reactToMessage = async (messageId, emoji) => {
    try {
      const res = await liveChatApi.post(`/api/chat/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not add reaction');
    }
  };

  const closeMessageMenu = () => {
    clearTimeout(reactionTimerRef.current);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setActiveMessageMenuId(null);
    setMessageMenuMode('controls');
    setEditingMessageId(null);
    setEditingText('');
  };

  const openMessageMenu = (msg) => {
    if (String(msg.id || '').startsWith('temp_')) return;
    setActiveMessageMenuId(msg.id);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setMessageMenuMode('controls');
    setVisibleTimeId(msg.id);
    setEditingMessageId(null);
    setEditingText('');
  };

  const copyMessageText = async (msg) => {
    const text = parseAgentTagFromBody(msg.body || '').body.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
      closeMessageMenu();
    } catch {
      toast.error('Copy failed');
    }
  };

  const startEditingMessage = (msg) => {
    setEditingMessageId(msg.id);
    setEditingText(parseAgentTagFromBody(msg.body || '').body);
  };

  const saveEditedMessage = async (messageId) => {
    const body = editingText.trim();
    if (!body) return;
    try {
      const original = messages.find(item => item.id === messageId);
      const originalAgentName = parseAgentTagFromBody(original?.body || '').agentName;
      const res = await liveChatApi.patch(`/api/chat/messages/${messageId}`, { body: withAgentTag(body, originalAgentName || activeAgentName) });
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not edit message');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const res = await liveChatApi.delete(`/api/chat/messages/${messageId}`);
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not delete message');
    }
  };

  const beginLongPress = (messageId) => {
    clearTimeout(reactionTimerRef.current);
    reactionTimerRef.current = setTimeout(() => {
      setActiveMessageMenuId(messageId);
      setActiveReactionId(null);
      setMessageMenuMode('controls');
      setVisibleTimeId(messageId);
    }, 450);
  };

  const cancelLongPress = () => {
    clearTimeout(reactionTimerRef.current);
  };

  const closeReactionsOnOutsideClick = (event) => {
    if (!activeReactionId && !activeMessageMenuId) return;
    const target = event.target;
    if (target?.closest?.('[data-chat-bubble], [data-reaction-tray], [data-message-controls], [data-message-actions]')) return;
    closeMessageMenu();
  };

  const openPhotoPreview = (item) => {
    setPhotoPreview(item);
    if (!previewHistoryRef.current) {
      window.history.pushState({ chatPhotoPreview: true }, '');
      previewHistoryRef.current = true;
    }
  };

  const closePhotoPreview = () => {
    if (previewHistoryRef.current) {
      previewHistoryRef.current = false;
      window.history.replaceState({ ...(window.history.state || {}), chatPhotoPreview: false }, '');
    }
    setPhotoPreview(null);
  };

  const deliveryStatusForMessage = (msg, isMe) => {
    if (!isMe) return null;
    if (String(msg.id || '').startsWith('temp_')) return msg.send_failed ? 'Failed' : 'Sending';
    return msg.read_by_staff ? 'Seen' : 'Delivered';
  };

  const renderReactionBadges = (msg, isMe) => {
    const entries = Object.entries(msg.reactions || {}).filter(([, users]) => users?.length).slice(-1);
    if (!entries.length) return null;
    return (
      <div className="absolute -bottom-2 -right-2 z-10 flex flex-wrap gap-1">
        {entries.map(([emoji, users]) => (
          <button
            key={emoji}
            type="button"
            onClick={() => reactToMessage(msg.id, emoji)}
            className="h-5 min-w-5 px-1 rounded-full text-[13px] flex items-center justify-center gap-0.5"
            style={{
              background: isMe ? 'rgba(0,132,255,0.82)' : 'rgba(15,23,42,0.10)',
              color: isMe ? '#FFFFFF' : '#111827',
              boxShadow: 'none',
              border: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif'
            }}
          >
            <span>{emoji}</span>
            {users.length > 1 && <span className="text-[9px] opacity-75">{users.length}</span>}
          </button>
        ))}
      </div>
    );
  };

  const renderQuickReactions = (msg) => (
    activeMessageMenuId === msg.id && messageMenuMode === 'emoji' ? (
    <div className="mb-1 flex flex-col items-start gap-1.5" data-reaction-tray>
      <div className="flex gap-1 opacity-95 rounded-full bg-white px-1.5 py-1 shadow-xl">
        {QUICK_REACTIONS.map(emoji => (
          <button
            key={emoji}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={() => reactToMessage(msg.id, emoji)}
            className="w-7 h-7 rounded-full text-[14px] transition hover:scale-110"
            style={{ color: '#111827', fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
            aria-label={`React ${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={() => setExpandedReactionId(current => current === msg.id ? null : msg.id)}
          className="w-7 h-7 rounded-full text-[18px] leading-none transition hover:bg-black/5"
          style={{ color: '#111827' }}
          aria-label="More reactions"
        >
          +
        </button>
      </div>
      {expandedReactionId === msg.id && (
        <div className="grid max-h-32 w-56 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl bg-white p-1.5 shadow-2xl">
          {IPHONE_EMOJIS.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={() => reactToMessage(msg.id, emoji)}
              className="h-7 rounded-xl text-[18px] transition hover:bg-black/5"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
              aria-label={`React ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
    ) : null
  );

  const renderMessageControls = (msg, isMe) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'controls' || msg.deleted) return null;
    return (
      <div
        className={`mt-1 flex items-center gap-1.5 ${isMe ? 'justify-end pr-3' : 'justify-start pl-3'}`}
        data-message-controls
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(msg.id);
            setMessageMenuMode('emoji');
            setExpandedReactionId(null);
          }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999, background: 'rgba(10,10,16,0.86)', border: '1px solid rgba(255,255,255,0.18)' }}
          aria-label="Open reactions"
        >
          <SmilePlus className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(null);
            setExpandedReactionId(null);
            setMessageMenuMode('actions');
          }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999, background: 'rgba(10,10,16,0.86)', border: '1px solid rgba(255,255,255,0.18)' }}
          aria-label="Open message actions"
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderMessageActions = (msg, isMe) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'actions' || msg.deleted) return null;
    const displayBody = parseAgentTagFromBody(msg.body || '').body;
    const canEdit = isMe && Boolean(displayBody.trim()) && !String(msg.id || '').startsWith('temp_');
    const canDelete = isMe && !String(msg.id || '').startsWith('temp_');
    const canCopy = Boolean(displayBody.trim());
    return (
      <div
        className={`mt-1 flex flex-col gap-1 ${isMe ? 'items-end' : 'items-start'}`}
        data-message-actions
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="w-40 overflow-hidden rounded-2xl bg-white py-1 shadow-xl">
          {canDelete && (
            <button
              type="button"
              onClick={() => deleteMessage(msg.id)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => startEditingMessage(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
          {canCopy && (
            <button
              type="button"
              onClick={() => copyMessageText(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Copy className="h-4 w-4" />
              Copy text
            </button>
          )}
        </div>
        {editingMessageId === msg.id && (
          <div className="flex w-[min(260px,78vw)] items-center gap-1 rounded-2xl bg-white p-1.5 shadow-xl">
            <input
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveEditedMessage(msg.id);
                }
                if (e.key === 'Escape') closeMessageMenu();
              }}
              className="min-w-0 flex-1 rounded-full bg-gray-100 px-3 py-2 text-[13px] text-gray-950 outline-none"
              autoFocus
            />
            <button
              type="button"
              onClick={() => saveEditedMessage(msg.id)}
              className="h-8 rounded-full bg-[#0084FF] px-3 text-[11px] font-bold text-white"
            >
              Save
            </button>
            <button type="button" onClick={closeMessageMenu} className="h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Cancel edit">
              <X className="mx-auto h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAttachments = (attachments = [], imageOnly = false) => {
    if (!attachments.length) return null;
    return (
      <div className={`${imageOnly ? '' : 'mt-2'} flex flex-wrap gap-2`}>
        {attachments.map(item => {
          const imageUrl = resolveMediaUrl(item.url || item.preview_url || (item.path ? `/uploads/${item.path}` : ''));
          if (!imageUrl) return null;
          return (
            <button key={item.id} type="button" onClick={() => openPhotoPreview({ ...item, url: imageUrl })} className="block text-left">
              <SafeImage src={imageUrl} alt={item.filename || 'chat upload'} className="max-w-[150px] max-h-[150px] object-cover rounded-xl" loading="lazy" fallback={null} />
            </button>
          );
        })}
      </div>
    );
  };

  const brandAvatarUrl = resolveMediaUrl(theme?.logo_url || theme?.intro_logo_url || conversation?.tenant_logo_url || '');
  const activeAgentName = selectedAgent?.name || conversation?.preferred_agent_name || agentNameFromSubject(conversation?.subject) || '';
  const activeAgentTitle = selectedAgent?.title || conversation?.preferred_agent_title || 'Support Agent';
  const activeAgentAvatarUrl = resolveMediaUrl(selectedAgent?.avatar_url || conversation?.preferred_agent_avatar_url || '');
  const avatarForMessage = (msg, isMe) => {
    if (isMe) return resolveMediaUrl(user?.avatar_url || brandAvatarUrl);
    return resolveMediaUrl(msg.target_agent_avatar_url || activeAgentAvatarUrl || msg.sender_avatar_url || conversation?.tenant_logo_url || brandAvatarUrl);
  };

  const renderAvatar = (url, fallback, className = 'w-7 h-7') => (
    <div className={`${className} rounded-full shrink-0 flex items-center justify-center text-[10px] font-black overflow-hidden`} style={{ background: '#fff', color: '#111827' }}>
      {url ? <SafeImage src={url} alt="" className="w-full h-full object-cover" loading="lazy" fallback={fallback} /> : fallback}
    </div>
  );

  const renderDateSeparator = (label) => label ? (
    <div className="flex justify-center py-5">
      <span
        className="rounded-full px-3.5 py-1 text-[12px] font-semibold"
        style={{
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: '#B7BFCC',
        }}
      >
        {label}
      </span>
    </div>
  ) : null;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col overflow-hidden overscroll-none"
      data-testid="chat-panel"
      onPointerDownCapture={closeReactionsOnOutsideClick}
      style={{
        background: 'var(--vl-bg, #000000)',
      }}
    >
      <SheetHeader
        className="sticky top-0 z-30 shrink-0 px-5 pr-20 pb-3 border-b"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)',
          borderColor: 'rgba(255,255,255,0.10)',
          background: 'var(--vl-bg, #000000)',
          boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="relative h-12 w-12 shrink-0 overflow-visible rounded-full"
            style={{ filter: 'drop-shadow(0 0 14px rgba(0,132,255,0.22))' }}
          >
            <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-full bg-white text-sm font-black text-gray-950">
              {activeAgentAvatarUrl ? (
                <SafeImage src={activeAgentAvatarUrl} alt="" className="h-full w-full object-cover" loading="lazy" fallback={(activeAgentName || 'S').charAt(0).toUpperCase()} />
              ) : (
                (activeAgentName || 'S').charAt(0).toUpperCase()
              )}
            </div>
            <span
              className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 bg-[#20F56B]"
              style={{ borderColor: 'var(--vl-bg, #000000)', boxShadow: '0 0 10px rgba(32,245,107,0.9)' }}
              aria-label="Agent online"
            />
          </div>
          <div className="min-w-0 text-left">
            <SheetTitle className="display truncate text-xl font-extrabold" style={{ color: '#fff' }}>{activeAgentName || 'Live Support'}</SheetTitle>
            <p className="mt-0.5 truncate text-xs uppercase tracking-[0.08em]" style={{ color: '#9CB8A2' }}>
              {activeAgentName ? (activeAgentTitle || 'Live Agent') : 'Live Agents'}
            </p>
          </div>
        </div>
      </SheetHeader>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-1" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-60" style={{ color: NEON }} />
            <p className="display font-semibold" style={{ color: '#fff' }}>Start a conversation</p>
            <p className="text-sm mt-1" style={{ color: '#9CB8A2' }}>
              {activeAgentName ? `Message will be delivered to ${activeAgentName}.` : 'Message will be delivered to support.'}
            </p>
          </div>
        ) : messages.map((msg, index) => {
          const isMe = msg.sender_role === 'player' || msg.sender_id === user?.id;
          const attachments = msg.attachments || [];
          const parsedBody = parseAgentTagFromBody(msg.body || '');
          const displayBody = parsedBody.body;
          const imageOnly = attachments.length > 0 && !displayBody;
          const avatarUrl = avatarForMessage(msg, isMe);
          const latestOwnMessageId = [...messages].reverse().find(item => item.sender_role === 'player' || item.sender_id === user?.id)?.id;
          const isLatestOwnMessage = isMe && msg.id === latestOwnMessageId;
          const messageStatus = deliveryStatusForMessage(msg, isMe);
          const separator = dateSeparatorLabel(msg.created_at, messages[index - 1]?.created_at);
          return (
            <div key={msg.id} className="contents">
              {renderDateSeparator(separator)}
              <div className={`mt-2.5 flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                {!isMe && renderAvatar(avatarUrl, (msg.sender_name || 'S').charAt(0).toUpperCase())}
                <div className={`max-w-[min(78%,420px)] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                  {renderQuickReactions(msg)}
                  {visibleTimeId === msg.id && (
                    <p className="mb-1 px-1 text-[10px]" style={{ color: '#C6CEDA' }}>
                      {formatChatTime(msg.created_at)}
                      {messageStatus && ` · ${messageStatus}`}
                      {msg.edited_at && !msg.deleted && ' · Edited'}
                    </p>
                  )}
                  <div className="relative w-fit max-w-full">
                    <div
                      data-chat-bubble
                      className="w-fit max-w-full select-none"
                      onMouseDown={() => beginLongPress(msg.id)}
                      onMouseUp={cancelLongPress}
                      onMouseLeave={cancelLongPress}
                      onTouchStart={() => beginLongPress(msg.id)}
                      onTouchEnd={cancelLongPress}
                      onClick={(e) => { e.preventDefault(); openMessageMenu(msg); }}
                      onContextMenu={(e) => { e.preventDefault(); openMessageMenu(msg); }}
                      style={{
                        background: imageOnly ? 'transparent' : (isMe ? '#0084FF' : '#FFFFFF'),
                        color: isMe ? '#FFFFFF' : '#111827',
                        borderRadius: imageOnly ? 0 : (isMe ? '18px 18px 7px 18px' : '18px 18px 18px 7px'),
                        border: 'none',
                        boxShadow: imageOnly ? 'none' : (isMe ? '0 1px 0 rgba(255,255,255,0.16) inset, 0 7px 18px rgba(0,42,180,0.18)' : 'none'),
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        touchAction: 'manipulation',
                        ...(isMe && !imageOnly ? { background: 'linear-gradient(180deg, #0B49FF 0%, #001EDB 100%)' } : {})
                      }}
                    >
                      <div className={imageOnly ? 'p-0' : 'px-3.5 py-1.5'}>
                        {msg.deleted ? (
                          <p className="text-[14px] italic opacity-70">Message deleted</p>
                        ) : displayBody && (
                          <p
                            className="text-[16px] font-medium leading-[1.32] whitespace-pre-wrap"
                            style={{ maxWidth: '31ch', overflowWrap: 'break-word', wordBreak: 'normal', hyphens: 'manual', textWrap: 'pretty', letterSpacing: 0 }}
                          >
                            {displayBody}
                          </p>
                        )}
                        {!msg.deleted && renderAttachments(attachments, imageOnly)}
                      </div>
                    </div>
                    {!msg.deleted && renderReactionBadges(msg, isMe)}
                  </div>
                  {renderMessageControls(msg, isMe)}
                  {renderMessageActions(msg, isMe)}
                  {isLatestOwnMessage && visibleTimeId !== msg.id && messageStatus && (
                    <p className="mt-0.5 px-1 text-[10px]" style={{ color: '#C6CEDA' }}>
                      {messageStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {agentTyping && (
        <div className="shrink-0 px-4 pb-2">
          <div className="flex items-center gap-2">
            {renderAvatar(activeAgentAvatarUrl, (agentTyping || 'A').charAt(0).toUpperCase(), 'w-6 h-6')}
            <div className="rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-700 shadow-sm">
              {agentTyping} is typing...
            </div>
          </div>
        </div>
      )}

      {photo && (
        <div className="px-4 py-2 border-t flex items-center gap-2 text-xs" style={{ borderColor: 'rgba(255,255,255,0.10)', color: '#C6CEDA' }}>
          <ImageIcon className="w-4 h-4" />
          <span className="flex-1 truncate">{photo.name}</span>
          <button onClick={() => { setPhoto(null); if (photoRef.current) photoRef.current.value = ''; }} aria-label="Remove photo">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {emojiOpen && (
        <div className="px-4 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
          <div className="grid max-h-36 w-64 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl p-1.5" style={{ background: 'rgba(245,245,247,0.96)', boxShadow: '0 -10px 30px rgba(0,0,0,0.2)' }}>
            {IPHONE_EMOJIS.map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                type="button"
                onClick={() => addEmoji(emoji)}
                className="h-7 rounded-xl text-[18px] hover:bg-black/5 transition"
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="shrink-0 px-4 pt-4 border-t"
        style={{
          borderColor: 'rgba(255,255,255,0.10)',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex items-center gap-2 rounded-full px-2 py-2" style={{ background: 'rgba(8,14,20,0.86)', border: '1px solid rgba(255,255,255,0.10)' }}>
        <input
          ref={photoRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={e => setPhoto(e.target.files?.[0] || null)}
          data-testid="player-chat-photo"
        />
        <Button type="button" onClick={() => photoRef.current?.click()} variant="outline" className="h-10 w-10 p-0 rounded-full" style={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', background: 'rgba(255,255,255,0.06)' }}>
          <Camera className="w-4 h-4" />
        </Button>
        <Button type="button" onClick={() => setEmojiOpen(open => !open)} variant="outline" className="h-10 w-10 p-0 rounded-full" style={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', background: 'rgba(255,255,255,0.06)' }} aria-label="Open emoji picker">
          <SmilePlus className="w-4 h-4" />
        </Button>
        <Input
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={stopTypingPulse}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message..."
          className="chat-composer-input h-10 flex-1 rounded-full border-transparent px-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          style={{ background: 'transparent', borderColor: 'transparent', color: '#fff', boxShadow: 'none' }}
          data-chat-input="composer"
          data-testid="player-chat-input"
        />
        <Button onClick={sendMessage} disabled={!input.trim() && !photo} className="h-10 w-11 p-0 rounded-full text-white" style={{ background: '#0084FF', boxShadow: 'none' }} data-testid="player-chat-send">
          <Send className="w-4 h-4" />
        </Button>
        </div>
      </div>

      {photoPreview && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(3,8,18,0.72)', backdropFilter: 'blur(12px)' }}
          onClick={closePhotoPreview}
        >
          <div
            className="relative w-full max-w-3xl max-h-[86vh] rounded-2xl p-3"
            style={{ background: '#0F1B2A', border: '1px solid rgba(255,255,255,0.18)', boxShadow: '0 24px 80px rgba(0,0,0,0.48)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePhotoPreview}
              className="absolute -top-3 -right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: '#FFFFFF', color: '#111827', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
              aria-label="Close photo preview"
            >
              <X className="w-5 h-5" />
            </button>
            <SafeImage
              src={photoPreview.url}
              alt={photoPreview.filename || 'chat photo preview'}
              className="w-full max-h-[78vh] object-contain rounded-xl"
              fallback={null}
            />
          </div>
        </div>
      )}
    </div>
  );
}
